import { useState } from "react";
import { Drawer, Form, Input, message, Spin, Select } from "antd";
import { CrownOutlined, AppstoreOutlined } from "@ant-design/icons";
import {
  drawerTitle,
  drawerFooter,
  DRAWER_FORM_WIDTH,
} from "../../../../components";
import {
  useCreateTenantMutation,
  useSystemPackagesQuery,
} from "../../../../Services";

interface Props {
  open: boolean;
  onClose: () => void;
}

export const CreateTenantDrawer: React.FC<Props> = ({ open, onClose }) => {
  const [form] = Form.useForm();
  const [creating, setCreating] = useState(false);

  const createTenantMutation = useCreateTenantMutation();
  const { data: packages = [], isLoading: packagesLoading } =
    useSystemPackagesQuery();

  const handleCreate = async () => {
    try {
      const values = await form.validateFields();
      setCreating(true);
      const res = await createTenantMutation.mutateAsync(values);
      if (res?.success) {
        message.success(res.message || "Tenant created successfully.");
        form.resetFields();
        onClose();
      } else {
        message.error(res?.message || "Failed to create tenant.");
      }
    } catch (err: any) {
      if (err?.errorFields) return;
      message.error(err?.message || "Something went wrong.");
    } finally {
      setCreating(false);
    }
  };

  const handleClose = () => {
    form.resetFields();
    onClose();
  };

  return (
    <Drawer
      title={drawerTitle("Create New Tenant")}
      open={open}
      onClose={handleClose}
      width={DRAWER_FORM_WIDTH}
      destroyOnClose
      footer={drawerFooter({
        actions: [
          {
            key: "cancel",
            label: "Cancel",
            onClick: handleClose,
          },
          {
            key: "create",
            label: creating ? "Creating..." : "Create Tenant",
            onClick: handleCreate,
            type: "primary",
            disabled: creating,
          },
        ],
      })}
    >
      <Spin spinning={creating} tip="Creating tenant...">
        <div className="space-y-4 px-1">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
            <div className="flex items-center gap-2 text-blue-700">
              <CrownOutlined className="text-blue-600" />
              <span className="text-sm font-medium">Tenant Information</span>
            </div>
            <div className="text-xs text-blue-600 mt-1">
              This tenant will have complete access to all features.
            </div>
          </div>

          <Form form={form} layout="vertical" className="space-y-3">
            <Form.Item
              name="name"
              label={<span className="font-medium">Tenant Name</span>}
              rules={[{ required: true, message: "Tenant name is required" }]}
            >
              <Input
                placeholder="e.g. Acme Corp"
                className="rounded-lg h-10 border-gray-300 hover:border-blue-400 focus:border-blue-500"
              />
            </Form.Item>

            <Form.Item
              name="tenant_sub_name"
              label={
                <span className="font-medium">Sub Name (URL identifier)</span>
              }
              rules={[{ required: true, message: "Sub name is required" }]}
            >
              <Input
                placeholder="e.g. acme-corp"
                className="rounded-lg h-10 border-gray-300 hover:border-blue-400 focus:border-blue-500"
              />
            </Form.Item>

            <Form.Item
              name="username"
              label={<span className="font-medium">Admin Username</span>}
              rules={[
                { required: true, message: "Username is required" },
                { min: 3, message: "At least 3 characters" },
              ]}
            >
              <Input
                placeholder="e.g. admin"
                className="rounded-lg h-10 border-gray-300 hover:border-blue-400 focus:border-blue-500"
              />
            </Form.Item>

            <Form.Item
              name="email"
              label={<span className="font-medium">Admin Email</span>}
              rules={[
                {
                  required: true,
                  type: "email",
                  message: "Valid email is required",
                },
              ]}
            >
              <Input
                placeholder="e.g. admin@acme.com"
                className="rounded-lg h-10 border-gray-300 hover:border-blue-400 focus:border-blue-500"
              />
            </Form.Item>

            <Form.Item
              name="password"
              label={<span className="font-medium">Admin Password</span>}
              rules={[
                { required: true, message: "Password is required" },
                { min: 6, message: "At least 6 characters" },
              ]}
            >
              <Input.Password
                placeholder="Min 6 characters"
                className="rounded-lg h-10 border-gray-300 hover:border-blue-400 focus:border-blue-500"
              />
            </Form.Item>

            <Form.Item
              name="package_id"
              label={
                <span className="font-medium">Assign Package (optional)</span>
              }
            >
              <Select
                allowClear
                placeholder="No package (all features enabled)"
                loading={packagesLoading}
                className="rounded-lg"
                options={packages.map((p: any) => ({
                  value: p.id,
                  label: `${p.name} — ${(Number(p.price) || 0).toLocaleString(
                    undefined,
                    { minimumFractionDigits: 2 },
                  )}`,
                }))}
              />
            </Form.Item>

            <div className="bg-purple-50 border border-purple-200 rounded-lg p-3 mt-2">
              <div className="flex items-center gap-2 text-purple-700">
                <AppstoreOutlined className="text-purple-600" />
                <span className="text-sm font-medium">POS Client Details</span>
              </div>
              <div className="text-xs text-purple-600 mt-1">
                A default client is created for this tenant in the POS. Leave
                blank to use the tenant name.
              </div>
            </div>

            <Form.Item
              name={["pos_client", "client_name"]}
              label={<span className="font-medium">Client Name</span>}
            >
              <Input
                placeholder="e.g. Acme Retail Client"
                className="rounded-lg h-10 border-gray-300 hover:border-purple-400 focus:border-purple-500"
              />
            </Form.Item>

            <Form.Item
              name={["pos_client", "contact_number"]}
              label={<span className="font-medium">Contact Number</span>}
            >
              <Input
                placeholder="e.g. +94771234567"
                className="rounded-lg h-10 border-gray-300 hover:border-purple-400 focus:border-purple-500"
              />
            </Form.Item>

            <Form.Item
              name={["pos_client", "email"]}
              label={<span className="font-medium">Email</span>}
            >
              <Input
                placeholder="e.g. client@acme.com"
                className="rounded-lg h-10 border-gray-300 hover:border-purple-400 focus:border-purple-500"
              />
            </Form.Item>

            <Form.Item
              name={["pos_client", "address"]}
              label={<span className="font-medium">Address</span>}
            >
              <Input
                placeholder="e.g. 123 Main St, Colombo"
                className="rounded-lg h-10 border-gray-300 hover:border-purple-400 focus:border-purple-500"
              />
            </Form.Item>
          </Form>
        </div>
      </Spin>
    </Drawer>
  );
};
